import PageTitle from "../Common/PageTitle"
import { FC, Fragment, useEffect, useState } from "react"
import {
    Box, Paper, Grid, TextField, Typography, IconButton, Button,
    Divider, Alert, CircularProgress
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { AddOutlined, DeleteOutlineOutlined } from "@mui/icons-material"
import { Apis, numberWithCommas } from "../../helpers"
import { EventsEmit } from "../../configs/events"
import { useLocation, useNavigate } from "react-router-dom"
import { Connect, mapStateToProps } from "../../Redux"
import { PropertyProps } from "../../types/properties"
import { VacationNoticeProps, DeductionProps } from "../../types/vacationNotices"
import ConfirmPinDialog from "../Common/ConfirmPinDialog"
import moment from 'moment'

interface Props {
    selectedProperty?: PropertyProps
}
interface StateProps {
    loading: boolean
    notice: VacationNoticeProps | null
    tenant: any
    deductions: DeductionProps[]
    resolutionNotes: string
    submitting: boolean
    error: string | null
    showPinDialog: boolean
    refunding: boolean
    pinError: string | null
    checkingStatus: boolean
}

const EDITABLE_STATUSES = ['PROCESSING', 'MAINTENANCE_REVIEW', 'DISPUTED']

const Process: FC<Props> = ({ selectedProperty }) => {
    const location = useLocation() as any
    const navigate = useNavigate()
    const { vacationNoticeId } = location.state || {}

    const [state, setState] = useState<StateProps>({
        loading: true,
        notice: null,
        tenant: null,
        deductions: [],
        resolutionNotes: '',
        submitting: false,
        error: null,
        showPinDialog: false,
        refunding: false,
        pinError: null,
        checkingStatus: false
    })
    const { loading, notice, tenant, deductions, resolutionNotes, submitting, error, showPinDialog, refunding, pinError, checkingStatus } = state

    const canManage = selectedProperty?.role === 'Owner' || selectedProperty?.role === 'Agent'

    const load = () => {
        Apis({ module: 'VacationNotices', action: 'startProcessing', vacationNoticeId })
            .then((resp: any) => {
                if (resp?.error)
                    return setState(s => ({ ...s, loading: false, error: resp.message }))
                setState(s => ({
                    ...s,
                    loading: false,
                    notice: resp.notice,
                    tenant: resp.tenant,
                    deductions: (resp.notice.deductions || []).map((d: any) => ({ description: d.description, amount: d.amount }))
                }))
            })
            .catch(() => setState(s => ({ ...s, loading: false, error: 'Failed to load this vacation notice.' })))
    }

    const refresh = (afterState?: Partial<StateProps>) => {
        Apis({ module: 'VacationNotices', action: 'getPropertyNotices', vacationNoticeId })
            .then((resp: any) => {
                if (resp?.error) return
                setState(s => ({
                    ...s,
                    ...afterState,
                    notice: resp.notice,
                    tenant: resp.tenant,
                    deductions: (resp.notice.deductions || []).map((d: any) => ({ description: d.description, amount: d.amount }))
                }))
            })
    }

    useEffect(() => {
        if (!vacationNoticeId) return setState(s => ({ ...s, loading: false, error: 'No vacation notice was specified.' }))
        load()
        // eslint-disable-next-line
    }, [])

    const totalDeductions = deductions.reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
    const refundableAmount = Math.max((notice?.depositPaid || 0) - totalDeductions, 0)
    const excessOwed = Math.max(totalDeductions - (notice?.depositPaid || 0), 0)

    const updateRow = (index: number, field: 'description' | 'amount', value: string) => {
        const rows = [...deductions]
        rows[index] = { ...rows[index], [field]: field === 'amount' ? value as any : value }
        setState(s => ({ ...s, deductions: rows }))
    }
    const addRow = () => setState(s => ({ ...s, deductions: [...s.deductions, { description: '', amount: 0 }] }))
    const removeRow = (index: number) => setState(s => ({ ...s, deductions: s.deductions.filter((_, i) => i !== index) }))

    const submitAssessment = () => {
        setState(s => ({ ...s, submitting: true, error: null }))
        Apis({
            module: 'VacationNotices',
            action: 'submitDeductions',
            vacationNoticeId,
            deductions: deductions.filter(d => d.description?.trim() && Number(d.amount) > 0),
            resolutionNotes
        }).then((resp: any) => {
            if (resp?.error) {
                EventsEmit('app', { info: { message: resp.message, status: 'error' } })
                return setState(s => ({ ...s, submitting: false }))
            }
            EventsEmit('app', { info: { message: resp.message, status: 'success' } })
            refresh({ submitting: false })
        }).catch(() => {
            setState(s => ({ ...s, submitting: false }))
            EventsEmit('app', { info: { message: 'Something went wrong. Please try again.', status: 'error' } })
        })
    }

    const initiateRefund = (pin: string) => {
        setState(s => ({ ...s, refunding: true, pinError: null }))
        Apis({ module: 'VacationNotices', action: 'initiateRefund', vacationNoticeId, pin })
            .then((resp: any) => {
                if (resp?.error) {
                    setState(s => ({ ...s, refunding: false, pinError: resp.message }))
                    return
                }
                EventsEmit('app', { info: { message: resp.message, status: 'success' } })
                refresh({ refunding: false, showPinDialog: false, pinError: null })
            }).catch(() => {
                setState(s => ({ ...s, refunding: false, pinError: 'Something went wrong. Please try again.' }))
            })
    }

    const checkRefundStatus = () => {
        setState(s => ({ ...s, checkingStatus: true }))
        refresh({ checkingStatus: false })
    }

    if (loading)
        return (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        )

    if (error || !notice)
        return (
            <Box sx={{ pr: 5 }}>
                <PageTitle title={"Process Vacation Notice"} backBtn />
                <Alert severity="error" sx={{ mt: 2 }}>{error || 'Vacation notice not found.'}</Alert>
            </Box>
        )

    const refund = notice.refund

    return (
        <Box sx={{ pr: 5, pb: 5 }}>
            <PageTitle title={"Process Vacation Notice"} backBtn />

            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2.5 }}>
                        <Typography sx={{ fontWeight: 'bold', mb: 1.5 }}>Tenant</Typography>
                        <Typography>{tenant?.firstName} {tenant?.lastName}</Typography>
                        <Typography sx={{ color: '#666', fontSize: 14 }}>{tenant?.phone}</Typography>
                        <Typography sx={{ color: '#666', fontSize: 14 }}>{tenant?.email}</Typography>
                        <Button
                            size="small"
                            sx={{ mt: 1, pl: 0 }}
                            onClick={() => navigate('/tenant', {
                                state: {
                                    user: {
                                        _id: tenant?._id,
                                        name: `${tenant?.firstName || ''} ${tenant?.lastName || ''}`.trim(),
                                        phone: tenant?.phone,
                                        email: tenant?.email,
                                        unit: { _id: notice.unitId, name: notice.unitName },
                                        deposit: notice.depositRequired,
                                        balance: 0,
                                        tenantId: notice.tenantId,
                                        userId: notice.tenantId,
                                        unitLabel: notice.unitLabel,
                                        labelId: notice.unitLabelId
                                    }
                                }
                            })}
                        >
                            View Full Payment History →
                        </Button>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2.5 }}>
                        <Typography sx={{ fontWeight: 'bold', mb: 1.5 }}>House</Typography>
                        <Typography>{notice.propertyName}</Typography>
                        <Typography sx={{ color: '#666', fontSize: 14 }}>Unit {notice.unitLabel} - {notice.unitName}</Typography>
                        <Typography sx={{ mt: 1.5 }}>Expected Move-Out Date</Typography>
                        <Typography sx={{ fontWeight: 'bold' }}>{moment(notice.moveOutDate).format('DD MMM YYYY')}</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>Deposit Assessment</Typography>
                            <Typography sx={{ fontSize: 14, color: '#666' }}>Deposit Paid: Ksh {numberWithCommas(notice.depositPaid)}</Typography>
                        </Box>

                        {notice.status === 'DISPUTED' && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                The tenant disputed this assessment: "{notice.reconciliation?.reason}"
                            </Alert>
                        )}

                        {EDITABLE_STATUSES.includes(notice.status) && canManage ? (
                            <Fragment>
                                {deductions.map((d, i) => (
                                    <Grid container spacing={1.5} key={i} sx={{ mb: 1, alignItems: 'center' }}>
                                        <Grid item xs={7}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Description"
                                                value={d.description}
                                                onChange={e => updateRow(i, 'description', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Amount (Ksh)"
                                                value={d.amount}
                                                onChange={e => updateRow(i, 'amount', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton onClick={() => removeRow(i)}><DeleteOutlineOutlined /></IconButton>
                                        </Grid>
                                    </Grid>
                                ))}
                                <Button startIcon={<AddOutlined />} onClick={addRow} sx={{ mt: 0.5 }}>
                                    Add Deduction
                                </Button>

                                <Divider sx={{ my: 2 }} />
                                <SummaryRows depositPaid={notice.depositPaid} totalDeductions={totalDeductions} refundableAmount={refundableAmount} excessOwed={excessOwed} />

                                <LoadingButton
                                    variant="contained"
                                    loading={submitting}
                                    onClick={submitAssessment}
                                    sx={{ mt: 3 }}
                                >
                                    {notice.status === 'DISPUTED' ? 'Resubmit Assessment' : 'Submit Assessment for Tenant Review'}
                                </LoadingButton>
                            </Fragment>
                        ) : (
                            <Fragment>
                                {notice.deductions.length === 0 ? (
                                    <Typography sx={{ color: '#888' }}>No deductions were made.</Typography>
                                ) : notice.deductions.map((d, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                                        <Typography>{d.description}</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>Ksh {numberWithCommas(d.amount)}</Typography>
                                    </Box>
                                ))}
                                <Divider sx={{ my: 2 }} />
                                <SummaryRows depositPaid={notice.depositPaid} totalDeductions={notice.totalDeductions} refundableAmount={notice.refundableAmount} excessOwed={notice.excessOwed} />
                            </Fragment>
                        )}
                    </Paper>
                </Grid>

                {notice.status === 'ACCEPTED' && canManage && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2.5 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1.5 }}>Deposit Refund</Typography>
                            {(!refund || refund.status === 'NONE') && (
                                <Fragment>
                                    <Typography sx={{ mb: 2 }}>Refundable amount: <b>Ksh {numberWithCommas(notice.refundableAmount)}</b></Typography>
                                    <Button
                                        variant="contained"
                                        disabled={notice.refundableAmount <= 0}
                                        onClick={() => setState(s => ({ ...s, showPinDialog: true, pinError: null }))}
                                    >
                                        Refund Deposit
                                    </Button>
                                </Fragment>
                            )}
                            {refund && (refund.status === 'PENDING' || refund.status === 'PROCESSING') && (
                                <Alert severity="warning" action={
                                    <Button size="small" onClick={checkRefundStatus} disabled={checkingStatus}>Check Status</Button>
                                }>
                                    Refund of Ksh {numberWithCommas(Number(refund.amount))} to {refund.phone} is being processed via M-Pesa.
                                </Alert>
                            )}
                            {refund?.status === 'COMPLETED' && (
                                <Alert severity="success">
                                    Ksh {numberWithCommas(Number(refund.amount))} was successfully refunded to {refund.phone}.
                                </Alert>
                            )}
                            {refund?.status === 'FAILED' && (
                                <Fragment>
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        Refund failed: {refund.failureReason || 'Unknown error'}
                                    </Alert>
                                    <Button variant="contained" onClick={() => setState(s => ({ ...s, showPinDialog: true, pinError: null }))}>
                                        Retry Refund
                                    </Button>
                                </Fragment>
                            )}
                        </Paper>
                    </Grid>
                )}

                {notice.status === 'COMPLETED' && (
                    <Grid item xs={12}>
                        <Alert severity="success">This vacation process has been completed{refund?.amount ? ` and Ksh ${numberWithCommas(Number(refund.amount))} was refunded to the tenant.` : '.'}</Alert>
                    </Grid>
                )}
            </Grid>

            {showPinDialog && (
                <ConfirmPinDialog
                    title="Confirm Deposit Refund"
                    message={`Enter your PIN to refund Ksh ${numberWithCommas(notice.refundableAmount)} to the tenant's M-Pesa number.`}
                    processing={refunding}
                    errorMessage={pinError}
                    onConfirm={initiateRefund}
                    close={() => setState(s => ({ ...s, showPinDialog: false, pinError: null }))}
                />
            )}
        </Box>
    )
}

const SummaryRows: FC<{ depositPaid: number, totalDeductions: number, refundableAmount: number, excessOwed?: number }> = ({ depositPaid, totalDeductions, refundableAmount, excessOwed }) => (
    <Box>
        <Row label="Deposit Paid" value={depositPaid} />
        <Row label="Total Deductions" value={totalDeductions} />
        {Boolean(excessOwed) && <Row label="Amount Owed by Tenant" value={excessOwed as number} color="error.main" />}
        <Row label="Refundable Deposit" value={refundableAmount} bold />
    </Box>
)
const Row: FC<{ label: string, value: number, bold?: boolean, color?: string }> = ({ label, value, bold, color }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
        <Typography sx={{ fontWeight: bold ? 'bold' : 'normal', color }}>{label}</Typography>
        <Typography sx={{ fontWeight: bold ? 'bold' : 600, color }}>Ksh {numberWithCommas(value)}</Typography>
    </Box>
)

export default Connect(mapStateToProps)(Process)
